import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, X, Minimize2, Maximize2, ArrowLeft } from 'lucide-react';
import { TranscriptChat } from './TranscriptChat';
import { Button } from '../ui/button';
interface TranscriptContext {
  content: string;
  title?: string;
  description?: string;
  channel_name?: string;
  platform?: string;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  repost_count?: number;
  upload_date?: string;
  duration?: number;
  quote?: string;
  tldr?: string[];
  top_comments?: Array<{
    text: string;
    likes: number;
  }>;
}
interface TranscriptChatSheetProps {
  transcript: TranscriptContext;
  transcriptId: string;
  initialQuestion?: string | null;
  onClose: () => void;
}
type SheetState = 'half' | 'full';
export const TranscriptChatSheet: React.FC<TranscriptChatSheetProps> = ({
  transcript,
  transcriptId,
  initialQuestion,
  onClose
}) => {
  const [sheetState, setSheetState] = useState<SheetState>('half');
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const sheetRef = useRef<HTMLDivElement>(null);
  // Calculate sheet height and styling based on state
  const getSheetStyles = () => {
    if (sheetState === 'full') {
      return {
        height: '100vh',
        borderRadius: '0'
      };
    }
    return {
      height: '60vh',
      borderRadius: '1.5rem 1.5rem 0 0'
    };
  };
  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent) => {
    // Don't allow dragging in full-screen mode
    if (sheetState === 'full') return;
    // Only allow dragging from the header area
    const target = e.target as HTMLElement;
    if (!target.closest('.drag-handle')) return;
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };
  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || sheetState === 'full') return;
    setCurrentY(e.touches[0].clientY);
  };
  // Handle touch end
  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const deltaY = currentY - startY;
    const threshold = 50; // pixels to trigger state change
    if (sheetState === 'half') {
      if (deltaY < -threshold) {
        // Swipe up -> Full screen
        setSheetState('full');
      } else if (deltaY > threshold) {
        // Swipe down -> Close
        onClose();
      }
    }
  };
  // Handle backdrop click (only in half mode)
  const handleBackdropClick = () => {
    if (sheetState === 'half') {
      onClose();
    }
  };
  // Prevent body scroll when sheet is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  // Animate in on mount
  const [isAnimatingIn, setIsAnimatingIn] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimatingIn(false), 300);
    return () => clearTimeout(timer);
  }, []);
  const styles = getSheetStyles();
  const isFullScreen = sheetState === 'full';
  return <>
      {/* Backdrop - darker in full-screen mode */}
      <div className={`fixed inset-0 z-[55] transition-all duration-300 ${isFullScreen ? 'bg-black/60 backdrop-blur-md' : 'bg-black/40 backdrop-blur-sm'}`} onClick={handleBackdropClick} style={{
      opacity: isAnimatingIn ? 0 : 1
    }} data-id="element-1711" />

      {/* Bottom Sheet / Full Screen Modal */}
      <div ref={sheetRef} className="fixed left-0 right-0 bg-background shadow-2xl z-[60] transition-all duration-300 ease-out" style={{
      bottom: 0,
      height: styles.height,
      borderRadius: styles.borderRadius,
      transform: isDragging ? `translateY(${Math.max(0, currentY - startY)}px)` : isAnimatingIn ? 'translateY(100%)' : 'translateY(0)'
    }} data-id="element-1712">
        {/* Header - Different styles for full-screen */}
        <div className={`drag-handle flex items-center justify-between px-4 border-b border-border/40 ${isFullScreen ? 'h-14 cursor-default' : 'flex-col pt-3 pb-2 cursor-grab active:cursor-grabbing'}`} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-1713">
          {/* Grabber - Only show in half mode */}
          {!isFullScreen && <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mb-3" data-id="element-1714" />}

          {/* Header Content */}
          <div className={`flex items-center justify-between w-full ${!isFullScreen && 'pb-2'}`} data-id="element-1715">
            <div className="flex items-center gap-2" data-id="element-1716">
              {isFullScreen && <Button variant="ghost" size="sm" onClick={() => setSheetState('half')} className="h-9 w-9 p-0 rounded-full mr-1" data-id="element-1717">
                  <ArrowLeft size={18} data-id="element-1718" />
                </Button>}
              <MessageSquare size={18} className="text-primary" data-id="element-1719" />
              <span className="font-semibold text-sm" data-id="element-1720">
                {isFullScreen ? 'Chat' : 'Chat'}
              </span>
            </div>
            <div className="flex items-center gap-1" data-id="element-1721">
              {!isFullScreen && <Button variant="ghost" size="sm" onClick={() => setSheetState('full')} className="h-8 w-8 p-0 rounded-full" data-id="element-1722">
                  <Maximize2 size={16} data-id="element-1723" />
                </Button>}
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 rounded-full" data-id="element-1724">
                <X size={18} data-id="element-1725" />
              </Button>
            </div>
          </div>
        </div>

        {/* Chat Content - Full viewport height minus header */}
        <div className={`overflow-hidden ${isFullScreen ? 'h-[calc(100vh-56px)]' : 'h-[calc(60vh-60px)]'}`} data-id="element-1726">
          <div className="h-full flex flex-col" data-id="element-1727">
            <TranscriptChat transcript={transcript} transcriptId={transcriptId} initialQuestion={initialQuestion} data-id="element-1728" />
          </div>
        </div>
      </div>
    </>;
};